import type { PortfolioHistoryPoint } from "@papertiger/contracts";

/** User-selectable equity history windows. */
export type EquityChartRange = "1d" | "1w" | "1m" | "all";

/** One chart-space coordinate generated from an equity observation. */
export interface EquityChartCoordinate {
  /** Horizontal SVG coordinate. */
  x: number;
  /** Vertical SVG coordinate. */
  y: number;
}

/** Geometry and value bounds needed to render the equity line. */
export interface EquityChartGeometry {
  /** Ordered chart-space coordinates. */
  coordinates: EquityChartCoordinate[];
  /** Lowest equity value in the series. */
  minimumEquityDollars: number;
  /** Highest equity value in the series. */
  maximumEquityDollars: number;
}

/**
 * Builds a monotone cubic SVG path through chart coordinates.
 *
 * The curve passes through every trade point and does not introduce a peak or
 * trough between points that are already moving in the same direction.
 *
 * @param coordinates Ordered chart-space coordinates.
 * @returns SVG path data for a lightly smoothed line.
 */
export function createSmoothedEquityChartPath(
  coordinates: EquityChartCoordinate[],
): string {
  const firstCoordinate = coordinates[0];
  if (firstCoordinate === undefined) return "";
  if (coordinates.length === 1) {
    return `M ${firstCoordinate.x.toFixed(1)} ${firstCoordinate.y.toFixed(1)}`;
  }
  let path = `M ${firstCoordinate.x.toFixed(1)} ${firstCoordinate.y.toFixed(1)}`;
  const slopes = coordinates.slice(1).map((coordinate, index) => {
    const previous = coordinates[index];
    const deltaX = coordinate.x - (previous?.x ?? coordinate.x);
    return deltaX === 0 ? null : (coordinate.y - (previous?.y ?? coordinate.y)) / deltaX;
  });
  const tangents = coordinates.map((coordinate, index) => {
    const previousSlope = slopes[index - 1] ?? null;
    const nextSlope = slopes[index] ?? null;
    if (index === 0) return nextSlope ?? 0;
    if (index === coordinates.length - 1) return previousSlope ?? 0;
    if (
      previousSlope === null ||
      nextSlope === null ||
      previousSlope * nextSlope <= 0
    ) {
      return 0;
    }
    const previous = coordinates[index - 1] ?? coordinate;
    const next = coordinates[index + 1] ?? coordinate;
    const previousWidth = coordinate.x - previous.x;
    const nextWidth = next.x - coordinate.x;
    return (3 * (previousWidth + nextWidth)) / (
      (2 * nextWidth + previousWidth) / previousSlope +
      (nextWidth + 2 * previousWidth) / nextSlope
    );
  });
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const current = coordinates[index];
    const next = coordinates[index + 1];
    if (current === undefined || next === undefined) continue;
    const width = next.x - current.x;
    if (width <= 0) {
      path += ` L ${next.x.toFixed(1)} ${next.y.toFixed(1)}`;
      continue;
    }
    const currentTangent = tangents[index] ?? 0;
    const nextTangent = tangents[index + 1] ?? 0;
    path += ` C ${(current.x + width / 3).toFixed(1)} ${(current.y + (currentTangent * width) / 3).toFixed(1)} ${(next.x - width / 3).toFixed(1)} ${(next.y - (nextTangent * width) / 3).toFixed(1)} ${next.x.toFixed(1)} ${next.y.toFixed(1)}`;
  }
  return path;
}

/** Millisecond durations for bounded equity history windows. */
const EQUITY_RANGE_DURATION_MILLISECONDS: Record<
  Exclude<EquityChartRange, "all">,
  number
> = {
  "1d": 24 * 60 * 60 * 1_000,
  "1w": 7 * 24 * 60 * 60 * 1_000,
  "1m": 30 * 24 * 60 * 60 * 1_000,
};

/**
 * Returns the inclusive history start timestamp for a selected range.
 *
 * @param range Selected equity range.
 * @param nowMilliseconds Current timestamp in milliseconds.
 * @returns ISO start timestamp, or undefined for all history.
 */
export function getEquityChartRangeStart(
  range: EquityChartRange,
  nowMilliseconds: number,
): string | undefined {
  if (range === "all") return undefined;
  return new Date(
    nowMilliseconds - EQUITY_RANGE_DURATION_MILLISECONDS[range],
  ).toISOString();
}

/**
 * Converts chronological equity observations to padded SVG coordinates.
 *
 * @param points Chronological equity observations.
 * @param width SVG view-box width.
 * @param height SVG view-box height.
 * @param padding Space reserved around the plotted line.
 * @returns Chart coordinates and source value bounds.
 */
export function createEquityChartGeometry(
  points: PortfolioHistoryPoint[],
  width: number,
  height: number,
  padding: number,
): EquityChartGeometry | null {
  if (points.length === 0) return null;
  const equityValues = points.map((point) => point.equityDollars);
  const minimumEquityDollars = Math.min(...equityValues);
  const maximumEquityDollars = Math.max(...equityValues);
  const equitySpan = maximumEquityDollars - minimumEquityDollars;
  const parsedTimestamps = points.map((point) => Date.parse(point.timestamp));
  const validTimestamps = parsedTimestamps.filter(Number.isFinite);
  const minimumTimestamp =
    validTimestamps.length === 0 ? 0 : Math.min(...validTimestamps);
  const maximumTimestamp =
    validTimestamps.length === 0 ? 0 : Math.max(...validTimestamps);
  const timestampSpan = maximumTimestamp - minimumTimestamp;
  const drawableWidth = width - padding * 2;
  const drawableHeight = height - padding * 2;
  const coordinates = points.map((point, index) => {
    const parsedTimestamp = parsedTimestamps[index] ?? Number.NaN;
    const x =
      timestampSpan > 0 && Number.isFinite(parsedTimestamp)
        ? padding +
          ((parsedTimestamp - minimumTimestamp) / timestampSpan) * drawableWidth
        : padding +
          (points.length === 1 ? drawableWidth : (index / (points.length - 1)) * drawableWidth);
    const y =
      equitySpan === 0
        ? padding + drawableHeight / 2
        : padding +
          ((maximumEquityDollars - point.equityDollars) / equitySpan) *
            drawableHeight;
    return { x, y };
  });
  return {
    coordinates,
    minimumEquityDollars,
    maximumEquityDollars,
  };
}
